import {forwardRef, Inject, Pipe, PipeTransform} from '@angular/core';
import * as moment from 'moment';
import {SettingsService} from '@app/core';
import { dateFromPhpDateTimeObject, localDateFromPhpDateField } from '@app/pipes/time.pipe';

/**
 * Formats timeline / activity dates (e.g. app-scheduled-activity due time) using regional settings.
 * Parses PHP JSON DateTime `{ date, timezone_type, timezone }`, unix timestamps (s or ms), and ISO strings.
 *
 * Verify from `v4/web/angularES`:
 *   npx ng build --configuration=development
 *   npx ng lint
 */
@Pipe({
  name: 'timelineDate'
})
export class TimelineDatePipe implements PipeTransform {

    public utcTimeString: any;
    constructor(@Inject(forwardRef(() => SettingsService)) private settings) {
    }

  transform(value: any, args?: any): any {
      const intlSettings = this.settings._csIntlSettings;

      let language = this.settings._csLanguage;
      if(language == 'en'){
          language = 'en-GB';
      }

      let formatString: string;
      let dateFormat: string;
      const longDate = parseInt(intlSettings.long_date);
      const defaultDateFormat =  language === 'en-GB' ? 'dddd Do MMM YYYY' : 'dddd MMM D YYYY'
      const formats = {
          0: 'ddd Do MMM YYYY',
          1: 'dddd Do MMM YYYY',
          2: 'ddd Do MMMM YYYY',
          3: 'dddd Do MMMM YYYY',
          50: 'ddd MMM D YYYY',
          51: 'dddd MMM D YYYY',
          52: 'ddd MMMM D YYYY',
          53: 'dddd MMMM D YYYY'
      };
      dateFormat = formats[longDate] || defaultDateFormat;

      let timeFormat: string;
      if (parseInt(intlSettings['time']) === 0) {
          timeFormat = 'h:mm a';
      } else if (parseInt(intlSettings['time']) === 1) {
          timeFormat = 'H:mm';
      } else {
          timeFormat = 'H:mm';
      }

      if (args == 'datetime') {
          formatString = dateFormat + ' (' + timeFormat + ')';
      } else if ( args == 'time') {
          formatString = '(' + timeFormat + ')' ;
      } else if (args == 'diarytime') {
          formatString = timeFormat;
      } else if (args == 'date') {
          formatString = dateFormat;
      } else if(args == 'diarydate'){
          formatString = dateFormat;
      }
      else {
          formatString = dateFormat;
      }

      // ISO instant with Z or numeric offset only (PHP `date` without offset uses local parse below).
      const isoRegexZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/;

      if(value && typeof value === 'object' && value.hasOwnProperty('_isAMomentObject')){
          if (args == 'diarytime' || args == 'diarydate') {
              this.utcTimeString = value;
          } else {
              this.utcTimeString = value._d;
          }
      } else if (value && typeof value === 'object' && value.hasOwnProperty('date')) {
          const dateStr = value.date;
          if (typeof dateStr !== 'string') {
              this.utcTimeString = new Date(NaN);
          } else {
              const fromPhp = dateFromPhpDateTimeObject(value);
              if (fromPhp) {
                  this.utcTimeString = fromPhp;
              } else {
                  this.utcTimeString = new Date(dateStr);
              }
          }
      } else if (value instanceof Date) {
          this.utcTimeString = value;
      } else {
        const strVal = typeof value === 'string' ? value : String(value);
        const phpParsed = localDateFromPhpDateField(strVal);
        if (isoRegexZ.test(strVal)) {
            this.utcTimeString = new Date(strVal);
        } else if (phpParsed) {
            // Wall time from API (e.g. scheduled due date); avoid UTC + toLocaleString round-trip
            this.utcTimeString = phpParsed;
        } else {
            this.utcTimeString  = new Date(strVal + ' UTC');
            this.utcTimeString = new Date(this.utcTimeString.toLocaleString(this.settings._csLanguage, { timeZone:  this.settings._csCountryCode.timezone }));
        }
      }

    // Never surface moment's literal "Invalid date" string in the UI.
    const m = moment(this.utcTimeString);
    return m.isValid() ? m.format(formatString) : '';
  }

}

@Pipe({
    name: 'timeStringFromSeconds'
})
export class TimeStringFromSeconds implements PipeTransform {

    transform(value: any, args?: any): any {
        let timeStr = '';
        let timeInSeconds = !isNaN(parseInt(value)) ? parseInt(value) : 0;
        if(timeInSeconds > 0) {
            let secondsToShow = timeInSeconds % 60;
            let minutes = timeInSeconds > 60 ? Math.floor(timeInSeconds / 60) : 0;
            let minutesToShow = minutes > 0 ? minutes % 60 : minutes;
            let hours = minutes > 60 ? Math.floor(minutes / 60) : 0;
            if(hours > 0) {
                let hourText = hours > 1 ? 'hours' : 'hour';
                timeStr += hours + ' ' + hourText;
            }
            if(minutesToShow > 0) {
                let text = minutesToShow > 1 ? 'minutes' : 'minute';
                timeStr += timeStr != '' ? ' ' : '';
                timeStr += minutesToShow + ' ' + text;
            }
            if(secondsToShow > 0) {
                let secText = secondsToShow > 1 ? 'seconds' : 'second';
                timeStr += timeStr != '' ? ' ' : '';
                timeStr += secondsToShow + ' ' + secText;
            }
        }

        return timeStr;
    }

}
